import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { stravaService } from 'src/service/strava-service'
import { OutlinedButton } from 'src/component/ui/button'

export const StravaConnection = (): React.ReactElement => {
  const [isConnected, setIsConnected] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isConnecting, setIsConnecting] = useState<boolean>(false)
  const [isDisconnecting, setIsDisconnecting] = useState<boolean>(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [searchParams] = useSearchParams()

  useEffect(() => {
    loadConnectionStatus()
    checkCallbackParams()
  }, [])

  const loadConnectionStatus = async (): Promise<void> => {
    try {
      const connected = await stravaService.getConnectionStatus()
      setIsConnected(connected)
    } catch (error) {
      console.error('Failed to load Strava connection status:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const checkCallbackParams = (): void => {
    const success = searchParams.get('success')
    const error = searchParams.get('error')

    if (success === 'strava_connected') {
      setMessage({ type: 'success', text: 'Successfully connected to Strava!' })
      setIsConnected(true)
    } else if (error) {
      const errorMessages: Record<string, string> = {
        strava_oauth_error: 'Failed to authorize with Strava. Please try again.',
        missing_code: 'Authorization code missing. Please try again.',
        user_not_found: 'User not found. Please try again.',
        callback_failed: 'Connection failed. Please try again.',
      }
      setMessage({
        type: 'error',
        text: errorMessages[error] || 'An error occurred. Please try again.',
      })
    }
  }

  const handleConnect = async (): Promise<void> => {
    setIsConnecting(true)
    setMessage(null)
    try {
      const authUrl = await stravaService.getAuthUrl()
      window.location.href = authUrl
    } catch (error) {
      console.error('Failed to initiate Strava connection:', error)
      setMessage({ type: 'error', text: 'Failed to connect to Strava. Please try again.' })
    } finally {
      setIsConnecting(false)
    }
  }

  const handleDisconnect = async (): Promise<void> => {
    setIsDisconnecting(true)
    setMessage(null)
    try {
      await stravaService.disconnect()
      setIsConnected(false)
      setMessage({ type: 'success', text: 'Successfully disconnected from Strava.' })
    } catch (error) {
      console.error('Failed to disconnect from Strava:', error)
      setMessage({ type: 'error', text: 'Failed to disconnect. Please try again.' })
    } finally {
      setIsDisconnecting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="text-gray-500">Loading...</div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      {message && (
        <div
          className={`mb-4 p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
          role="alert"
        >
          {message.text}
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <img src="/strava.svg" alt="Strava" width="120" height="40" />
          <div className={`text-sm ${isConnected ? 'text-green-600' : 'text-gray-500'}`}>
            {isConnected ? 'Connected' : 'Not Connected'}
          </div>
        </div>
      </div>

      <div className="mb-6">
        <h4 className="font-semibold mb-2">What gets shared:</h4>
        <ul className="space-y-2 text-sm text-gray-600">
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>Completed workouts automatically posted to Strava</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>Workout title and duration</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>Workout thumbnail image</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>Workout description and trainer name</span>
          </li>
        </ul>
      </div>

      {isConnected ? (
        <OutlinedButton onClick={handleDisconnect} disabled={isDisconnecting} isLoading={isDisconnecting}>
          Disconnect from Strava
        </OutlinedButton>
      ) : (
        <OutlinedButton onClick={handleConnect} disabled={isConnecting} isLoading={isConnecting}>
          Connect to Strava
        </OutlinedButton>
      )}

      <p className="text-xs text-gray-500 mt-4 italic">Note: Disconnecting will stop automatic workout posting to Strava.</p>
    </div>
  )
}
